
import { useEffect } from "react";
import {
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  Users,
  ShieldCheck,
  Target,
  Eye,
  BookOpen,
  ArrowUpRight,
  Landmark,
  Sparkles,
  UserRound,
  Network,
} from "lucide-react";
import { motion } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

const members = [
  {
    id: 1,
    image: "/assets/img/bgen-Iqbal.jpeg",
    name: "BGen Shah Md. Sultan Uddin Iqbal, PhD. Bir Protik(RTD)",
    designation_name: "Chairman, Bandarban University Trust",
    department_name: "Member",
  },
  {
    id: 2,
    image: "/assets/img/nurul-absar.jpeg",
    name: "Dr. Mohammad Nurul Absar",
    designation_name: "Member Secretary, Bandarban University Trust",
    department_name: "Member",
  },
  {
    id: 3,
    image: "/assets/img/nurul-islam-bu.jpeg",
    name: "Professor Dr. M. Nurul Islam",
    designation_name: "Treasurer, Bandarban University",
    department_name: "Member",
  },
  {
    id: 4,
    image: "/assets/img/kazi-ahmed.jpeg",
    name: "Prof. Dr. Kazi Ahmed Nabi",
    designation_name:
      "Dean, School of Business Administration, Bandarban University",
    department_name: "Member",
  },
  {
    id: 5,
    image: "/assets/img/sarwar-jahan.jpeg",
    name: "Professor Sarwar Jahan",
    designation_name: "Member, Bandarban University Trust",
    department_name: "Member",
  },
  {
    id: 6,
    image: "/assets/img/nasirullah.jpeg",
    name: "Prof. Dr. Amir Mohammad Nasrullah",
    designation_name: "Member, Bandarban University Trust",
    department_name: "Member",
  },
  {
    id: 7,
    image: "/assets/images/teacher.jpg",
    name: "Chief Executive Officer",
    designation_name: "Bandarban Hill District Council, Bandarban",
    department_name: "Member",
  },
  {
    id: 8,
    image: "https://web.bubban.ac.bd/assets/teacher/2005.jpeg",
    name: "Muhammad Wahidur Rahman",
    designation_name:
      "Coordinator, Department of Governance & Development Studies, Bandarban University",
    department_name: "Member",
  },
  {
    id: 9,
    image: "https://web.bubban.ac.bd/assets/teacher/2010.jpeg",
    name: "Sohrab Hossain",
    designation_name:
      "Coordinator, Department of English, Bandarban University",
    department_name: "Member",
  },
  {
    id: 10,
    image: "https://web.bubban.ac.bd/assets/teacher/2002.jpeg",
    name: "Mohammad Amir Saadat",
    designation_name:
      "Coordinator, Department of Computer Science & Engineering, Bandarban University",
    department_name: "Member",
  },
  {
    id: 11,
    image: "https://web.bubban.ac.bd/assets/teacher/2014.jpeg",
    name: "Ching Chi Prue Sukey",
    designation_name:
      "Coordinator, Department of Business Administration, Bandarban University",
    department_name: "Member",
  },
  {
    id: 12,
    image: "https://web.bubban.ac.bd/assets/teacher/2008.png",
    name: "Umme Sheame Shahataz",
    designation_name:
      "Coordinator, Department of Hospitality & Tourism Management, Bandarban University",
    department_name: "Member",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.09 },
  },
};

function ProfileCard({ image, name, designation, label, featured = false }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -8 }}
      className={`group relative overflow-hidden rounded-3xl border bg-white transition-all duration-500 ${
        featured
          ? "border-emerald-100 shadow-xl shadow-emerald-900/10"
          : "border-gray-100 shadow-md hover:shadow-2xl hover:shadow-emerald-900/10"
      }`}
    >
      <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50 to-gray-100">
        <div
          className={`relative mx-auto flex items-center justify-center overflow-hidden ${
            featured ? "h-80 sm:h-96" : "h-64"
          }`}
        >
          <img
            src={image}
            alt={name}
            loading="lazy"
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = LogoFallback;
              e.currentTarget.classList.add("object-contain", "p-10");
            }}
          />
        </div>

        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/30 to-transparent" />

        <div className="absolute left-4 top-4">
          <span className="rounded-full border border-white/40 bg-white/90 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-emerald-800 shadow-sm backdrop-blur">
            {label}
          </span>
        </div>
      </div>

      <div className={`relative ${featured ? "p-7 sm:p-8" : "p-5"}`}>
        <div className="mb-3 h-1 w-12 rounded-full bg-emerald-600 transition-all duration-500 group-hover:w-20" />

        <h3
          className={`font-bold leading-snug text-gray-900 transition-colors group-hover:text-emerald-800 ${
            featured ? "text-xl sm:text-2xl" : "text-lg"
          }`}
        >
          {name}
        </h3>

        <p className="mt-3 text-sm leading-relaxed text-gray-600">
          {designation}
        </p>

        <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4">
          <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-700">
            <UserRound size={15} />
            {label}
          </span>

          <ArrowUpRight
            size={18}
            className="text-gray-300 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-emerald-700"
          />
        </div>
      </div>
    </motion.div>
  );
}

function PrincipleCard({ icon: Icon, title, description, number }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -7 }}
      className="group relative overflow-hidden rounded-3xl border border-gray-100 bg-white p-7 shadow-sm transition-all duration-500 hover:border-emerald-200 hover:shadow-xl"
    >
      <span className="absolute -right-3 -top-8 text-8xl font-black text-emerald-50 transition-transform duration-500 group-hover:scale-110">
        {number}
      </span>

      <div className="relative z-10">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 transition-all duration-500 group-hover:bg-emerald-700 group-hover:text-white group-hover:rotate-6">
          <Icon size={27} />
        </div>

        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        <p className="mt-3 text-sm leading-7 text-gray-600">
          {description}
        </p>
      </div>
    </motion.div>
  );
}

export default function AcPage({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Academic Council | Bandarban University",
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const chairman = {
    image: "/assets/img/vc-bubban.jpg",
    name: "Professor Dr. Mohammad Mohibullah Siddiquee",
    designation: "Vice Chancellor, Bandarban University",
  };

  const secretary = {
    image: "/assets/img/jashim.jpeg",
    name: "Prof. Md. Jashim Uddin Khan",
    designation: "Registrar, Bandarban University",
  };

  return (
    <div
      className="overflow-hidden bg-[#f7faf8] text-gray-800"
      style={{ "--university-accent": accent }}
    >
      {/* HERO SECTION */}
      <section className="relative isolate overflow-hidden bg-[#073c27] text-white">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -left-32 -top-40 h-96 w-96 rounded-full border-[45px] border-emerald-300" />
          <div className="absolute -bottom-48 -right-20 h-[500px] w-[500px] rounded-full border-[65px] border-emerald-300" />
        </div>

        <div className="absolute inset-0 bg-gradient-to-r from-[#063b27] via-[#075437]/95 to-[#08734d]/80" />

        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 70, repeat: Infinity, ease: "linear" }}
          className="absolute right-[8%] top-10 hidden h-64 w-64 rounded-full border border-white/10 lg:block"
        >
          <div className="absolute -left-2 top-1/2 h-4 w-4 rounded-full bg-emerald-300 shadow-lg shadow-emerald-300/50" />
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div
              variants={fadeUp}
              className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-emerald-100 backdrop-blur"
            >
              <Landmark size={16} />
              Bandarban University
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-7xl"
            >
              Academic
              <span className="block text-emerald-300">Council</span>
            </motion.h1>

            <motion.div
              variants={fadeUp}
              className="mt-7 h-1.5 w-24 rounded-full bg-emerald-300"
            />

            <motion.p
              variants={fadeUp}
              className="mt-7 max-w-2xl text-base leading-8 text-emerald-50/90 sm:text-lg"
            >
              Guiding the future with vision, leadership, and integrity.
              Advancing academic excellence through collective wisdom,
              responsible governance, and a commitment to quality education.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="mt-9 flex flex-wrap gap-3"
            >
              <a
                href="#council-members"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 font-bold text-emerald-900 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-emerald-50"
              >
                <Users size={18} />
                Meet the Council
                <ArrowUpRight size={17} />
              </a>

              <div className="flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-3 text-sm font-medium text-white backdrop-blur">
                <GraduationCap size={19} />
                Academic Leadership
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="absolute bottom-10 right-10 hidden lg:block"
          >
            <div className="flex h-36 w-36 items-center justify-center rounded-full border border-white/20 bg-white/5 backdrop-blur">
              <div className="flex h-28 w-28 items-center justify-center rounded-full border border-emerald-300/40 bg-emerald-400/10">
                <BookOpen size={48} className="text-emerald-200" />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 h-2 w-full bg-gradient-to-r from-emerald-300 via-white/30 to-emerald-500" />
      </section>

      {/* INTRODUCTION / CHAIRMAN */}
      <section className="relative px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            className="mb-12 text-center"
          >
            <span className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-700">
              Academic Leadership
            </span>
            <h2 className="mt-3 text-3xl font-black text-gray-900 sm:text-4xl">
              Chairman of the Academic Council
            </h2>
            <div className="mx-auto mt-5 h-1 w-20 rounded-full bg-emerald-600" />
          </motion.div>

          <div className="grid items-center gap-10 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -45 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative mx-auto w-full max-w-lg"
            >
              <div className="absolute -inset-4 rounded-[2rem] bg-emerald-100/70" />
              <div className="absolute -left-7 -top-7 h-24 w-24 rounded-tl-[2rem] border-l-4 border-t-4 border-emerald-600" />
              <div className="relative overflow-hidden rounded-[1.8rem] border-8 border-white bg-white shadow-2xl">
                <img
                  src={chairman.image}
                  alt={chairman.name}
                  className="h-[390px] w-full object-cover object-top sm:h-[460px]"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = LogoFallback;
                    e.currentTarget.classList.add("object-contain", "p-10");
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-6 pt-20 text-white">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">
                    Chairman
                  </span>
                  <h3 className="mt-2 text-xl font-bold">
                    {chairman.name}
                  </h3>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 45 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:pl-5"
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-2 text-xs font-bold uppercase tracking-widest text-emerald-800">
                <ShieldCheck size={15} />
                Council Chairman
              </span>

              <h2 className="mt-6 text-3xl font-black leading-tight text-gray-900 sm:text-4xl">
                {chairman.name}
              </h2>

              <p className="mt-4 text-lg font-semibold text-emerald-700">
                {chairman.designation}
              </p>

              <div className="my-7 h-1 w-16 rounded-full bg-emerald-600" />

              <p className="text-base leading-8 text-gray-600">
                The Academic Council is an important academic body of
                Bandarban University. Under the leadership of its Chairman,
                the Council supports academic development, quality assurance,
                curriculum advancement, and the continuous improvement of
                teaching and learning.
              </p>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm">
                  <GraduationCap className="mb-3 text-emerald-700" size={26} />
                  <h4 className="font-bold text-gray-900">Academic Vision</h4>
                  <p className="mt-1 text-xs leading-5 text-gray-500">
                    Promoting academic excellence
                  </p>
                </div>

                <div className="rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm">
                  <Network className="mb-3 text-emerald-700" size={26} />
                  <h4 className="font-bold text-gray-900">Leadership</h4>
                  <p className="mt-1 text-xs leading-5 text-gray-500">
                    Collaborative academic governance
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COUNCIL MEMBERS */}
      <section
        id="council-members"
        className="relative bg-white px-6 py-20 lg:px-10"
      >
        <div className="absolute right-0 top-0 h-72 w-72 rounded-bl-full bg-emerald-50/70" />

        <div className="relative mx-auto max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-14 flex flex-col justify-between gap-5 md:flex-row md:items-end"
          >
            <div>
              <span className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-700">
                Our Academic Leaders
              </span>
              <h2 className="mt-3 text-3xl font-black text-gray-900 sm:text-4xl">
                Members of the Council
              </h2>
              <div className="mt-5 h-1 w-20 rounded-full bg-emerald-600" />
              <p className="mt-5 max-w-2xl leading-7 text-gray-500">
                Meet the distinguished members contributing their knowledge,
                experience, and leadership to the academic development of
                Bandarban University.
              </p>
            </div>

            <div className="flex w-fit items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-700 text-white">
                <Users size={23} />
              </div>
              <div>
                <p className="text-2xl font-black text-emerald-900">
                  {members.length}
                </p>
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                  Council Members
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.05 }}
            variants={stagger}
            className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {members.map((member) => (
              <ProfileCard
                key={member.id}
                image={member.image}
                name={member.name}
                designation={member.designation_name}
                label={member.department_name}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* MEMBER SECRETARY */}
      <section className="relative overflow-hidden bg-[#073c27] px-6 py-20 text-white lg:px-10">
        <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full border-[45px] border-white/5" />
        <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full border-[45px] border-emerald-300/10" />

        <div className="relative mx-auto max-w-6xl">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-300/30 bg-emerald-300/10 px-4 py-2 text-xs font-bold uppercase tracking-widest text-emerald-200">
                <ShieldCheck size={15} />
                Member Secretary
              </span>

              <h2 className="mt-6 text-3xl font-black leading-tight sm:text-4xl">
                {secretary.name}
              </h2>

              <p className="mt-4 text-lg font-semibold text-emerald-300">
                {secretary.designation}
              </p>

              <div className="my-7 h-1 w-16 rounded-full bg-emerald-300" />

              <p className="max-w-lg leading-8 text-emerald-50/80">
                The Member Secretary supports the coordination and
                administrative activities of the Academic Council, helping
                facilitate effective communication and academic governance.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative mx-auto w-full max-w-sm"
            >
              <div className="absolute -inset-4 rounded-[2rem] border border-emerald-300/30" />
              <div className="relative overflow-hidden rounded-[1.8rem] border-8 border-white/10 shadow-2xl">
                <img
                  src={secretary.image}
                  alt={secretary.name}
                  className="h-[390px] w-full object-cover object-top"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = LogoFallback;
                    e.currentTarget.classList.add("object-contain", "p-10", "bg-white");
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-5 pt-16">
                  <p className="text-xs font-bold uppercase tracking-widest text-emerald-300">
                    Member Secretary
                  </p>
                  <p className="mt-1 text-lg font-bold">{secretary.name}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VISION MISSION GOALS */}
      <section className="px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-12 text-center"
          >
            <span className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-700">
              Our Direction
            </span>
            <h2 className="mt-3 text-3xl font-black text-gray-900 sm:text-4xl">
              Our Vision, Mission & Goals
            </h2>
            <div className="mx-auto mt-5 h-1 w-20 rounded-full bg-emerald-600" />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid gap-7 md:grid-cols-3"
          >
            <PrincipleCard
              icon={Eye}
              number="01"
              title="Our Vision"
              description="To become a leading institution of higher learning in Asia."
            />
            <PrincipleCard
              icon={Sparkles}
              number="02"
              title="Our Mission"
              description="To nurture creativity, research, and leadership through quality education."
            />
            <PrincipleCard
              icon={Target}
              number="03"
              title="Our Goals"
              description="Expanding research opportunities and strengthening global partnerships."
            />
          </motion.div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#06452e] via-[#08734d] to-[#0b8056] px-6 py-16 text-white lg:px-10">
        <div className="absolute -right-20 -top-32 h-96 w-96 rounded-full border-[55px] border-white/5" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative mx-auto max-w-7xl"
        >
          <div className="mb-10 text-center">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-200">
              Get in Touch
            </span>
            <h2 className="mt-3 text-3xl font-black sm:text-4xl">
              Office of Academic Council
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-7 text-emerald-50/80">
              Contact Bandarban University for academic council-related
              information and official enquiries.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            <div className="flex items-start gap-4 rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur transition-all hover:bg-white/15">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-800">
                <MapPin size={23} />
              </div>
              <div>
                <h3 className="font-bold">Our Location</h3>
                <p className="mt-2 text-sm leading-6 text-emerald-50/80">
                  Rowadaw Heights Main Road, Bandarban town
                </p>
              </div>
            </div>

            <a
              href="tel:+8801318305900"
              className="flex items-start gap-4 rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:bg-white/15"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-800">
                <Phone size={23} />
              </div>
              <div>
                <h3 className="font-bold">Phone Number</h3>
                <p className="mt-2 text-sm text-emerald-50/80">
                  +880 1318-305900
                </p>
              </div>
            </a>

            <a
              href="mailto:info@bubban.ac.bd"
              className="flex items-start gap-4 rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:bg-white/15"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-800">
                <Mail size={23} />
              </div>
              <div>
                <h3 className="font-bold">Email Address</h3>
                <p className="mt-2 break-all text-sm text-emerald-50/80">
                  info@bubban.ac.bd
                </p>
              </div>
            </a>
          </div>

          <div className="mt-10 text-center">
            <a
              href="mailto:info@bubban.ac.bd?subject=Academic%20Council%20Enquiry"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 font-bold text-emerald-900 shadow-xl transition-all duration-300 hover:-translate-y-1 hover:bg-emerald-50"
            >
              <Mail size={18} />
              Contact the University
              <ArrowUpRight size={17} />
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
